import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApiResponse, Cliente, DocumentoCliente, TareaConRelaciones, EstadoTarea, ESTADO_LABELS } from '../types';
import { useKanban } from '../context/KanbanContext';
import { toast } from '../lib/toast';
import { apiFetch } from '../lib/apiFetch';

interface DocumentosClientePanelProps {
    cliente: Cliente;
    onClose: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const ICONOS_POR_EXTENSION: Record<string, string> = {
    pdf: '📄',
    doc: '📝', docx: '📝',
    xls: '📊', xlsx: '📊', csv: '📊',
    ppt: '📽️', pptx: '📽️',
    txt: '📃', md: '📃',
    png: '🖼️', jpg: '🖼️', jpeg: '🖼️',
    zip: '🗜️'
};

const obtenerIcono = (nombreArchivo: string): string => {
    const ext = nombreArchivo.split('.').pop()?.toLowerCase() || '';
    return ICONOS_POR_EXTENSION[ext] || '📎';
};

const formatearTamanio = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatearFecha = (fecha: string): string => {
    return new Date(fecha).toLocaleDateString('es-CL', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

export const DocumentosClientePanel: React.FC<DocumentosClientePanelProps> = ({ cliente, onClose }) => {
    const { swimlanes, fetchKanbanBoard, vincularDocumentoTarea } = useKanban();
    const [documentos, setDocumentos] = useState<DocumentoCliente[]>([]);
    const [loading, setLoading] = useState(true);
    const [subiendo, setSubiendo] = useState(false);
    const [eliminandoId, setEliminandoId] = useState<number | null>(null);
    const [tareaAVincular, setTareaAVincular] = useState<Record<number, number | ''>>({});
    const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const tareasCliente: TareaConRelaciones[] = useCallback(() => {
        const out: TareaConRelaciones[] = [];
        swimlanes.forEach(sw => {
            Object.values(sw.tareas).forEach(arr => arr.forEach(t => {
                if (t.cliente_id === cliente.id && !t.archivado) out.push(t);
            }));
        });
        return out.sort((a, b) => a.titulo.localeCompare(b.titulo));
    }, [swimlanes, cliente.id])();

    const fetchDocumentos = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes/${cliente.id}/documentos`);
            const result: ApiResponse<DocumentoCliente[]> = await response.json();

            if (result.success && result.data) {
                setDocumentos(result.data);
            }
        } catch (err) {
            console.error('Error al cargar documentos:', err);
        }
    }, [cliente.id]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchDocumentos();
            setLoading(false);
        })();
    }, [fetchDocumentos]);

    const handleSubir = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setSubiendo(true);
        setMensaje(null);

        try {
            const formData = new FormData();
            formData.append('archivo', file);

            const response = await apiFetch(`${API_BASE_URL}/clientes/${cliente.id}/documentos`, {
                method: 'POST',
                body: formData
            });

            const result: ApiResponse<DocumentoCliente> = await response.json();

            if (result.success) {
                await fetchDocumentos();
                setMensaje({ tipo: 'ok', texto: 'Documento subido correctamente.' });
            } else {
                setMensaje({ tipo: 'error', texto: result.error || 'No se pudo subir el documento.' });
            }
        } catch (err) {
            console.error('Error al subir documento:', err);
            setMensaje({ tipo: 'error', texto: 'No se pudo subir el documento.' });
        } finally {
            setSubiendo(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleVincular = async (docId: number) => {
        const tareaId = tareaAVincular[docId];
        if (tareaId === undefined || tareaId === '') return;
        const ok = await vincularDocumentoTarea(docId, typeof tareaId === 'number' ? tareaId : null);
        if (ok) {
            await fetchDocumentos();
            setTareaAVincular(prev => { const n = { ...prev }; delete n[docId]; return n; });
            setMensaje({ tipo: 'ok', texto: 'Documento vinculado.' });
        } else {
            setMensaje({ tipo: 'error', texto: 'No se pudo vincular el documento.' });
        }
    };

    const handleEliminar = async (documento: DocumentoCliente) => {
        if (!await toast.confirm({
            title: `¿Eliminar el documento "${documento.nombre_original}"?`,
            confirmText: 'Eliminar',
            type: 'warning'
        })) return;

        setEliminandoId(documento.id);
        try {
            const response = await apiFetch(`${API_BASE_URL}/documentos/${documento.id}`, { method: 'DELETE' });
            const result: ApiResponse<{ id: number }> = await response.json();

            if (result.success) {
                setDocumentos(prev => prev.filter(d => d.id !== documento.id));
                setMensaje({ tipo: 'ok', texto: 'Documento eliminado correctamente.' });
            } else {
                setMensaje({ tipo: 'error', texto: result.error || 'No se pudo eliminar el documento.' });
            }
        } catch (err) {
            console.error('Error al eliminar documento:', err);
            setMensaje({ tipo: 'error', texto: 'No se pudo eliminar el documento.' });
        } finally {
            setEliminandoId(null);
        }
    };

    // Descarga con auth: un <a href> plano no puede llevar el header
    // Authorization, así que se trae el archivo como blob autenticado y
    // se dispara la descarga desde JS.
    const handleDescargar = async (documento: DocumentoCliente) => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/documentos/${documento.id}/descargar`);
            if (!response.ok) {
                setMensaje({ tipo: 'error', texto: 'No se pudo descargar el documento.' });
                return;
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = documento.nombre_original;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar documento:', err);
            setMensaje({ tipo: 'error', texto: 'No se pudo descargar el documento.' });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-lg flex-shrink-0">
                            📄
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-slate-800">Documentos</h2>
                            <p className="text-xs text-slate-500 truncate">{cliente.empresa}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                    {mensaje && (
                        <div className={`text-sm rounded-xl px-4 py-2.5 border ${
                            mensaje.tipo === 'ok'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                            {mensaje.texto}
                        </div>
                    )}

                    <label className={`flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-2 border-dashed rounded-xl transition-colors cursor-pointer ${
                        subiendo
                            ? 'border-slate-200 text-slate-400 cursor-wait'
                            : 'border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600'
                    }`}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleSubir}
                            disabled={subiendo}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.png,.jpg,.jpeg,.zip,.md"
                        />
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                        </svg>
                        {subiendo ? 'Subiendo...' : 'Subir documento (PDF, Word, Excel, TXT, etc. — máx. 10MB)'}
                    </label>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Cargando documentos...</p>
                        </div>
                    ) : documentos.length === 0 ? (
                        <p className="text-center text-sm text-slate-500 py-6">Este cliente todavía no tiene documentos.</p>
                    ) : (
                        documentos.map(doc => (
                            <div key={doc.id} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                                <div className="flex items-center gap-3 flex-wrap gap-y-2">
                                    <div className="text-2xl flex-shrink-0">{obtenerIcono(doc.nombre_original)}</div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-slate-700 truncate" title={doc.nombre_original}>
                                            {doc.nombre_original}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {formatearTamanio(doc.tamanio_bytes)} · {formatearFecha(doc.subido_en)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                            onClick={() => handleDescargar(doc)}
                                            className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                                        >
                                            Descargar
                                        </button>
                                        <button
                                            onClick={() => handleEliminar(doc)}
                                            disabled={eliminandoId === doc.id}
                                            className="px-3 py-1.5 text-xs font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                                        >
                                            {eliminandoId === doc.id ? '...' : 'Eliminar'}
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 pl-11 flex-wrap gap-y-1.5">
                                    {doc.tarea_id ? (
                                        <>
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                                📎 #{doc.tarea_id} {doc.tarea_titulo ? `· ${doc.tarea_titulo.slice(0, 35)}${doc.tarea_titulo.length > 35 ? '…' : ''}` : ''}
                                            </span>
                                            {doc.tarea_estado && (
                                                <span className="text-[10px] text-slate-500">
                                                    ({ESTADO_LABELS[doc.tarea_estado as EstadoTarea]})
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-[10px] text-slate-500 italic">Sin vincular a tarea</span>
                                    )}
                                    <div className="flex items-center gap-1 ml-auto">
                                        <select
                                            value={tareaAVincular[doc.id] ?? ''}
                                            onChange={e => setTareaAVincular(prev => ({ ...prev, [doc.id]: e.target.value === '' ? '' : Number(e.target.value) }))}
                                            className="text-[10px] px-1.5 py-0.5 border border-slate-300 rounded-md bg-white max-w-[160px]"
                                        >
                                            <option value="">{doc.tarea_id ? 'Cambiar…' : 'Vincular a…'}</option>
                                            {tareasCliente.map(t => (
                                                <option key={t.id} value={t.id}>
                                                    #{t.id} {t.titulo.slice(0, 30)}
                                                </option>
                                            ))}
                                        </select>
                                        {tareaAVincular[doc.id] !== undefined && tareaAVincular[doc.id] !== '' && (
                                            <button
                                                onClick={() => handleVincular(doc.id)}
                                                className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                OK
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex items-center justify-end p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
